import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, CreditCard, Clock, Plus, Trash2, Edit, Calendar as CalendarIcon, MapPin, ArrowLeft, XCircle, Loader2, FileDown, Bug, Settings2, Info } from 'lucide-react';
import { bookingsService, outingsService } from '../../../services/jacanaDbService';
import { bookingActionsService, generateClientIdempotencyKey, type PartialRefundPaymentPayloadResponse } from '../../../services/bookingActionsService';
import { downloadOutingReportAsXlsx } from '../../../services/outingReportService';
import { Booking, BookingStatus, Outing, bookingOccupiesCapacity, resolveOutingPublicationStatus } from '../types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import useAppUser from '../../../hooks/useAppUser';
import ConfirmDialog from '../components/ConfirmDialog';
import ReservationsAdminList from '../components/admin/ReservationsAdminList';
import SupportTicketDialog from '../../support/components/SupportTicketDialog';
import HelpTrigger from '../../help/components/HelpTrigger';
import { Button, TextField } from '@mui/material';
import { organizationService } from '../../../services/organizationService';
import { outingPublicationService } from '../../../services/outingPublicationService';
import { isValidBelgianIban, normalizeIban } from '../../../utils/iban';
import { getUserFacingErrorMessage } from '../../../utils/userFacingError';
import { getStartingPrice } from '../pricing/pricingDisplay';

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  showOrganizerMessageField?: boolean;
  onConfirm: () => void | Promise<void>;
} | null;

type BookingWithLines = Booking & {
  moneyTotals?: {
    currentTotalDue?: number;
    totalPaid?: number;
    totalOutstanding?: number;
    totalRefundable?: number;
  };
  bookingLines?: Array<{
    rateId?: string;
    labelSnapshot?: string;
    unitPriceSnapshot?: number;
    quantityActive?: number;
  }>;
};

type ActiveBookingLine = {
  rateId: string;
  labelSnapshot: string;
  unitPriceSnapshot: number;
  quantityActive: number;
};

type PartialCancellationSelectionSummary = {
  activeLines: ActiveBookingLine[];
  cancellationByRate: Record<string, number>;
  totalParticipantsToCancel: number;
  estimatedRefund: number;
};

type BookingMoneySummary = {
  currentTotalDue: number;
  totalPaid: number;
  totalOutstanding: number;
  totalRefundable: number;
};

type PartialCancellationDraft = {
  booking: BookingWithLines;
  cancellationByRate: Record<string, number>;
  reason: string;
  idempotencyKey: string;
  iban: string;
  accountHolderName: string;
} | null;

type TotalCancellationDraft = {
  booking: Booking;
  iban: string;
  accountHolderName: string;
} | null;

type PartialRefundDraft = {
  booking: Booking;
  refusalReason: string;
} | null;

type OutingAnnouncementPreview = {
  outingId: string;
  publicationStatus: 'published' | 'draft';
  emailTargetCount: number;
  pushUserTargetCount: number;
  pushTokenTargetCount: number;
  canSendEmail: boolean;
  canSendPush: boolean;
} | null;

type OutingAnnouncementSummary = {
  emailTargeted: number;
  emailSucceeded: number;
  emailFailed: number;
  pushTargeted: number;
  pushSucceeded: number;
  pushFailed: number;
} | null;

type RefundBankDetailsSectionProps = {
  estimatedAmount: number | null;
  iban: string;
  accountHolderName: string;
  optional?: boolean;
  onIbanChange: (value: string) => void;
  onAccountHolderNameChange: (value: string) => void;
};

function isTerminalAnnouncementStatus(status: string | null | undefined) {
  return ['completed', 'partial_failed', 'failed'].includes(String(status || ''));
}

function RefundBankDetailsSection({
  estimatedAmount,
  iban,
  accountHolderName,
  optional = false,
  onIbanChange,
  onAccountHolderNameChange,
}: RefundBankDetailsSectionProps) {
  const normalizedIban = normalizeIban(iban);
  const hasTypedIban = iban.trim().length > 0;
  const showsIbanError = hasTypedIban && !isValidBelgianIban(normalizedIban);

  return (
    <div className="space-y-4 rounded-[20px] border border-sky-200 bg-sky-50/70 p-4">
      <div className="text-sm text-sky-900/90 leading-relaxed">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold">Coordonnées de remboursement client</p>
          <HelpTrigger
            articleId="booking.partial_refund.iban_validation"
            category="booking.partial_refund"
            label="Aide sur la vérification IBAN"
          />
        </div>
        <p className="mt-1">
          {optional
            ? "Si vous connaissez déjà l'IBAN du client, vous pouvez l'enregistrer directement maintenant. Sinon, le dossier restera en attente d'IBAN client."
            : "Renseignez l'IBAN belge du client et le nom du titulaire pour finaliser la demande de remboursement."}
        </p>
        <p className="mt-2">
          Montant estimé: <strong>{estimatedAmount ?? 0} €</strong>
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
          IBAN {optional ? '(optionnel)' : ''}
        </label>
        <input
          type="text"
          value={iban}
          onChange={(event) => onIbanChange(event.target.value)}
          placeholder="BE68 5390 0754 7034"
          className="w-full rounded-[20px] border border-brand-primary/10 bg-white/85 px-4 py-3 text-sm text-brand-primary font-mono placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
        />
        {showsIbanError ? (
          <p className="text-xs text-red-600">IBAN belge invalide.</p>
        ) : (
          <p className="text-xs text-brand-primary/60">Format attendu : BE + 14 chiffres</p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
          Nom du titulaire {optional ? '(optionnel)' : ''}
        </label>
        <input
          type="text"
          value={accountHolderName}
          onChange={(event) => onAccountHolderNameChange(event.target.value)}
          placeholder="Nom et prénom du titulaire"
          className="w-full rounded-[20px] border border-brand-primary/10 bg-white/85 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
        />
      </div>
    </div>
  );
}

function getActiveBookingLines(booking: Pick<BookingWithLines, 'bookingLines'>): ActiveBookingLine[] {
  if (!Array.isArray(booking.bookingLines)) {
    return [];
  }

  return booking.bookingLines
    .map((line) => {
      const rateId = String(line?.rateId || '').trim();
      const labelSnapshot = String(line?.labelSnapshot || '').trim() || 'Tarif';
      const unitPriceSnapshot = Number(line?.unitPriceSnapshot);
      const quantityActive = Math.max(0, Math.trunc(Number(line?.quantityActive) || 0));

      if (!rateId || quantityActive <= 0 || !Number.isFinite(unitPriceSnapshot) || unitPriceSnapshot < 0) {
        return null;
      }

      return {
        rateId,
        labelSnapshot,
        unitPriceSnapshot,
        quantityActive,
      };
    })
    .filter((line): line is ActiveBookingLine => Boolean(line));
}

function summarizePartialCancellationSelection(
  booking: Pick<BookingWithLines, 'bookingLines'>,
  selectedCancellationByRate: Record<string, number> | null | undefined,
): PartialCancellationSelectionSummary {
  const activeLines = getActiveBookingLines(booking);
  const cancellationByRate: Record<string, number> = {};
  let totalParticipantsToCancel = 0;
  let estimatedRefund = 0;

  for (const line of activeLines) {
    const requestedQuantity = Math.max(
      0,
      Math.min(line.quantityActive, Math.trunc(Number(selectedCancellationByRate?.[line.rateId]) || 0)),
    );

    if (requestedQuantity <= 0) {
      continue;
    }

    cancellationByRate[line.rateId] = requestedQuantity;
    totalParticipantsToCancel += requestedQuantity;

    if (line.unitPriceSnapshot > 0) {
      estimatedRefund += line.unitPriceSnapshot * requestedQuantity;
    }
  }

  return {
    activeLines,
    cancellationByRate,
    totalParticipantsToCancel,
    estimatedRefund: Math.round((estimatedRefund + Number.EPSILON) * 100) / 100,
  };
}

function getBookingMoneySummary(booking: Booking | BookingWithLines): BookingMoneySummary | null {
  const moneyTotals = (booking as BookingWithLines).moneyTotals;
  if (!moneyTotals) {
    return null;
  }

  const currentTotalDue = Number(moneyTotals.currentTotalDue);
  const totalPaid = Number(moneyTotals.totalPaid);
  const totalOutstanding = Number(moneyTotals.totalOutstanding);
  const totalRefundable = Number(moneyTotals.totalRefundable);

  if (
    !Number.isFinite(currentTotalDue)
    || !Number.isFinite(totalPaid)
    || !Number.isFinite(totalOutstanding)
    || !Number.isFinite(totalRefundable)
  ) {
    return null;
  }

  return {
    currentTotalDue: Math.max(0, Math.round(currentTotalDue * 100) / 100),
    totalPaid: Math.max(0, Math.round(totalPaid * 100) / 100),
    totalOutstanding: Math.max(0, Math.round(totalOutstanding * 100) / 100),
    totalRefundable: Math.max(0, Math.round(totalRefundable * 100) / 100),
  };
}

export default function AdminDashboard() {
  const { role, loading: authLoading } = useAppUser();
  const navigate = useNavigate();
  const [currentTimeMs] = useState(() => Date.now());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [outings, setOutings] = useState<Outing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOutingId, setSelectedOutingId] = useState<string | null>(null);
  const [bookingActionId, setBookingActionId] = useState<string | null>(null);
  const [outingActionId, setOutingActionId] = useState<string | null>(null);
  const [exportingOutingId, setExportingOutingId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [organizerMessageDraft, setOrganizerMessageDraft] = useState('');
  const [partialCancellationDraft, setPartialCancellationDraft] = useState<PartialCancellationDraft>(null);
  const [totalCancellationDraft, setTotalCancellationDraft] = useState<TotalCancellationDraft>(null);
  const [partialRefundDraft, setPartialRefundDraft] = useState<PartialRefundDraft>(null);
  const [partialRefundPayload, setPartialRefundPayload] = useState<PartialRefundPaymentPayloadResponse | null>(null);
  const [orgBankAccount, setOrgBankAccount] = useState('');
  const [savingOrgBankAccount, setSavingOrgBankAccount] = useState(false);
  const [announcementPreview, setAnnouncementPreview] = useState<OutingAnnouncementPreview>(null);
  const [announcementPreviewLoading, setAnnouncementPreviewLoading] = useState(false);
  const [announcementPreviewError, setAnnouncementPreviewError] = useState<string | null>(null);
  const [announcementSendLoading, setAnnouncementSendLoading] = useState(false);
  const organizerMessageRef = useRef('');

  const loadDashboardData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [bookingsData, outingsData] = await Promise.all([
        bookingsService.getAll(),
        outingsService.getAll({ includeDeleted: true })
      ]);
      const organization = await organizationService.getById().catch(() => null);

      let resolvedOutings = outingsData;
      if (selectedOutingId && !outingsData.some((outing) => outing.id === selectedOutingId)) {
        const selectedOutingSnapshot = await outingsService.getById(selectedOutingId);
        if (selectedOutingSnapshot) {
          resolvedOutings = [selectedOutingSnapshot, ...outingsData];
        }
      }

      const sortedOutings = [...resolvedOutings].sort((left, right) => {
        const leftTimestamp = Date.parse(left.date || '');
        const rightTimestamp = Date.parse(right.date || '');
        const safeLeftTimestamp = Number.isFinite(leftTimestamp) ? leftTimestamp : 0;
        const safeRightTimestamp = Number.isFinite(rightTimestamp) ? rightTimestamp : 0;
        return safeRightTimestamp - safeLeftTimestamp;
      });

      setBookings(bookingsData);
      setOutings(sortedOutings);
      setOrgBankAccount(organization?.paymentSettings?.bankAccount || '');
    } catch (error) {
      console.error('Failed to load data', error);
      toast.error("Erreur lors du chargement du tableau de bord.");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [selectedOutingId]);

  useEffect(() => {
    if (authLoading) return;

    if (!role || !['Admin', 'Patron', 'Developpeur'].includes(role)) {
      navigate('/', { replace: true });
      return;
    }

    queueMicrotask(() => {
      void loadDashboardData();
    });
  }, [authLoading, role, navigate, loadDashboardData]);


  const closeConfirmDialog = () => {
    setConfirmDialog(null);
    setOrganizerMessageDraft('');
    organizerMessageRef.current = '';
  };

  const handleConfirmDialog = async () => {
    if (!confirmDialog) return;
    const action = confirmDialog.onConfirm;
    await action();
    setConfirmDialog(null);
  };

  const resolveBookingStatus = (booking: Booking): BookingStatus => {
    const rawStatus = booking.bookingStatus || booking.status;
    switch (rawStatus) {
      case 'pending_payment':
      case 'pending_validation':
      case 'confirmed':
      case 'cancelled':
      case 'expired':
      case 'failed':
        return rawStatus;
      default:
        return 'pending_validation';
    }
  };

  const resolvePaymentStatus = (booking: Booking) => {
    return booking.paymentStatus || (booking.isPaid ? 'paid' : 'pending');
  };

  const resolveAttendanceStatus = (booking: Booking) => {
    return booking.attendanceStatus || 'expected';
  };

  const resolveRefundStatus = (booking: Booking) => {
    return booking.refundStatus || 'not_applicable';
  };

  const isOutingPast = (outing?: Outing | null) => {
    if (!outing?.date) return false;
    return new Date(outing.date).getTime() <= currentTimeMs;
  };

  const getPublicationBadgeProps = (outing?: Pick<Outing, 'publicationStatus'> | null) => {
    const publicationStatus = resolveOutingPublicationStatus(outing);

    if (publicationStatus === 'draft') {
      return {
        status: publicationStatus,
        label: 'Brouillon',
        className: 'border-amber-200 bg-amber-50 text-amber-700',
      };
    }

    if (publicationStatus === 'published') {
      return {
        status: publicationStatus,
        label: 'Publié',
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      };
    }

    return {
      status: publicationStatus,
      label: 'Invalide',
      className: 'border-slate-200 bg-slate-100 text-slate-700',
    };
  };

  const canValidateManualPayment = (booking: Booking) => {
    const moneySummary = getBookingMoneySummary(booking);
    return booking.paymentMethod === 'bank_transfer'
      && resolveBookingStatus(booking) === 'pending_validation'
      && resolvePaymentStatus(booking) === 'pending'
      && moneySummary !== null;
  };

  const canMarkAttendance = (booking: Booking) => {
    const attendanceStatus = resolveAttendanceStatus(booking);
    return isOutingPast(selectedOuting)
      && resolveBookingStatus(booking) === 'confirmed'
      && attendanceStatus !== 'attended'
      && attendanceStatus !== 'no_show';
  };

  const canCancelBooking = (booking: Booking) => {
    const bookingStatus = resolveBookingStatus(booking);
    const outingDateSource = selectedOuting?.id === booking.outingId
      ? selectedOuting?.date
      : outings.find((outing) => outing.id === booking.outingId)?.date;

    if (!outingDateSource) return false;

    const msUntilOuting = new Date(outingDateSource).getTime() - currentTimeMs;
    return ['confirmed', 'pending_validation'].includes(String(bookingStatus))
      && msUntilOuting > 24 * 60 * 60 * 1000;
  };

  const getTotalParticipantsCount = (booking: Booking) => {
    return Math.max(0, Math.trunc(Number(booking.participantsCount) || 0));
  };

  const getCancelledParticipantsCount = (booking: Booking) => {
    const total = getTotalParticipantsCount(booking);
    const rawCancelled = Number(booking.cancelledParticipantsCount);
    if (Number.isFinite(rawCancelled)) {
      return Math.max(0, Math.min(total, Math.trunc(rawCancelled)));
    }
    return 0;
  };

  const getActiveParticipantsCount = (booking: Booking) => {
    const total = getTotalParticipantsCount(booking);
    const rawActive = Number(booking.activeParticipantsCount);

    if (Number.isFinite(rawActive)) {
      return Math.max(0, Math.min(total, Math.trunc(rawActive)));
    }

    return Math.max(0, total - getCancelledParticipantsCount(booking));
  };

  const canCancelBookingParticipants = (booking: Booking) => {
    return canCancelBooking(booking)
      && getActiveParticipantsCount(booking) > 1
      && getActiveBookingLines(booking as BookingWithLines).length > 0;
  };

  const shouldCollectRefundBankDetailsForTotal = (booking: Booking) => {
    const moneySummary = getBookingMoneySummary(booking);
    return resolvePaymentStatus(booking) === 'paid'
      && moneySummary !== null
      && moneySummary.totalRefundable > 0;
  };

  const shouldCollectRefundBankDetailsForPartial = (booking: BookingWithLines, cancellationByRate: Record<string, number>) => {
    const summary = summarizePartialCancellationSelection(booking, cancellationByRate);
    return resolvePaymentStatus(booking) === 'paid'
      && summary.estimatedRefund > 0;
  };

  const getOptionalRefundBankDetailsValidationError = (iban: string, accountHolderName: string) => {
    const normalizedIban = normalizeIban(iban);
    const hasIban = normalizedIban.length > 0;
    const hasAccountHolderName = accountHolderName.trim().length > 0;

    if (!hasIban && !hasAccountHolderName) {
      return null;
    }

    if (!hasIban) {
      return 'L\'IBAN est requis si vous renseignez le titulaire.';
    }

    if (!isValidBelgianIban(normalizedIban)) {
      return 'IBAN belge invalide.';
    }

    if (!hasAccountHolderName) {
      return 'Le nom du titulaire est requis si vous renseignez un IBAN.';
    }

    return null;
  };

  const shouldSubmitAdminRefundBankDetails = (iban: string, accountHolderName: string) => {
    return normalizeIban(iban).length > 0 && accountHolderName.trim().length > 0;
  };

  const openPartialCancellationDialog = (booking: Booking) => {
    const bookingWithLines = booking as BookingWithLines;

    if (!getActiveBookingLines(bookingWithLines).length) {
      toast.error('Annulation partielle indisponible: détail tarifaire manquant pour cette réservation.');
      return;
    }

    setPartialCancellationDraft({
      booking: bookingWithLines,
      cancellationByRate: {},
      reason: '',
      idempotencyKey: generateClientIdempotencyKey('pc_admin'),
      iban: '',
      accountHolderName: booking.partialRefund?.accountHolderName || booking.userName || '',
    });
  };

  const openTotalCancellationDialog = (booking: Booking) => {
    setTotalCancellationDraft({
      booking,
      iban: '',
      accountHolderName: booking.partialRefund?.accountHolderName || booking.userName || '',
    });
  };

  const resolvePartialRefundStatus = (booking: Booking) => {
    const nestedStatus = String(booking.partialRefund?.status || '').trim();
    if (nestedStatus) return nestedStatus;
    const topLevelStatus = String(booking.partialRefundStatus || '').trim();
    if (topLevelStatus) return topLevelStatus;
    return null;
  };

  const canProcessRefund = (booking: Booking) => {
    const refundStatus = resolveRefundStatus(booking);
    const partialRefundState = resolvePartialRefundStatus(booking);
    const hasActivePartialRefundWorkflow = Boolean(partialRefundState && partialRefundState !== 'not_applicable');

    return resolveBookingStatus(booking) === 'cancelled'
      && resolvePaymentStatus(booking) === 'paid'
      && refundStatus !== 'refunded'
      && refundStatus !== 'refused'
      && !hasActivePartialRefundWorkflow;
  };

  const canProcessPartialRefund = (booking: Booking) => {
    return resolvePartialRefundStatus(booking) === 'pending_manual';
  };

  const closePartialRefundDialog = () => {
    if (bookingActionId) return;
    setPartialRefundDraft(null);
    setPartialRefundPayload(null);
  };

  const openPartialRefundDialog = async (booking: Booking) => {
    setPartialRefundDraft({ booking, refusalReason: '' });
    setPartialRefundPayload(null);
    setBookingActionId(booking.id);

    try {
      const payload = await bookingActionsService.getPartialRefundPaymentPayload({ bookingId: booking.id });
      setPartialRefundPayload(payload);
    } catch (error: unknown) {
      setPartialRefundDraft(null);
      setPartialRefundPayload(null);
      toast.error(getUserFacingErrorMessage(error, 'Impossible de charger les informations de remboursement partiel.', { audience: 'admin' }));
    } finally {
      setBookingActionId(null);
    }
  };

  const handleMarkPartialRefunded = async () => {
    if (!partialRefundDraft) return;

    setBookingActionId(partialRefundDraft.booking.id);
    try {
      await bookingActionsService.processPartialRefund({
        bookingId: partialRefundDraft.booking.id,
        action: 'mark_refunded',
      });

      await loadDashboardData(false);
      setPartialRefundDraft(null);
      setPartialRefundPayload(null);
      toast.success('Remboursement partiel marqué comme effectué.');
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible de valider le remboursement partiel.', { audience: 'admin' }));
    } finally {
      setBookingActionId(null);
    }
  };

  const handleRefusePartialRefund = async () => {
    if (!partialRefundDraft) return;

    const reason = partialRefundDraft.refusalReason.trim();
    if (!reason) {
      toast.error('Le motif de refus est obligatoire.');
      return;
    }

    setBookingActionId(partialRefundDraft.booking.id);
    try {
      await bookingActionsService.processPartialRefund({
        bookingId: partialRefundDraft.booking.id,
        action: 'refuse',
        reason,
      });

      await loadDashboardData(false);
      setPartialRefundDraft(null);
      setPartialRefundPayload(null);
      toast.success('Remboursement partiel refusé.');
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible de refuser le remboursement partiel.', { audience: 'admin' }));
    } finally {
      setBookingActionId(null);
    }
  };

  const handleCancelBooking = async () => {
    if (!totalCancellationDraft) return;

    const booking = totalCancellationDraft.booking;
    const shouldCollectRefundBankDetails = shouldCollectRefundBankDetailsForTotal(booking);
    const normalizedIban = normalizeIban(totalCancellationDraft.iban);
    const accountHolderName = totalCancellationDraft.accountHolderName.trim();

    if (shouldCollectRefundBankDetails) {
      const validationError = getOptionalRefundBankDetailsValidationError(normalizedIban, accountHolderName);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setBookingActionId(booking.id);
    try {
      const result = await bookingActionsService.cancelBooking({ bookingId: booking.id });

      if (
        shouldCollectRefundBankDetails
        && result.partialRefundStatus === 'pending_customer_iban'
        && Number(result.refundAmountRequested || 0) > 0
        && shouldSubmitAdminRefundBankDetails(normalizedIban, accountHolderName)
      ) {
        try {
          await bookingActionsService.submitPartialRefundIban({
            bookingId: booking.id,
            iban: normalizedIban,
            accountHolderName,
          });
          toast.success('Réservation annulée. Coordonnées de remboursement client enregistrées.');
        } catch (ibanError: unknown) {
          toast.error(getUserFacingErrorMessage(
            ibanError,
            'Réservation annulée, mais impossible d\'enregistrer l\'IBAN client. Le dossier reste en attente d\'IBAN.',
            { audience: 'admin' },
          ));
        }
      } else {
        toast.success(result.refundRequired
          ? 'Réservation annulée. Un remboursement reste à traiter.'
          : 'Réservation annulée.');
      }

      await loadDashboardData(false);
      setTotalCancellationDraft(null);
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible d’annuler cette réservation.', { audience: 'admin' }));
    } finally {
      setBookingActionId(null);
    }
  };

  const handleCancelBookingParticipants = async () => {
    if (!partialCancellationDraft) return;

    const booking = partialCancellationDraft.booking;
    const selectionSummary = summarizePartialCancellationSelection(booking, partialCancellationDraft.cancellationByRate);
    const shouldCollectRefundBankDetails = shouldCollectRefundBankDetailsForPartial(booking, partialCancellationDraft.cancellationByRate);
    const normalizedIban = normalizeIban(partialCancellationDraft.iban);
    const accountHolderName = partialCancellationDraft.accountHolderName.trim();

    if (!selectionSummary.activeLines.length) {
      toast.error('Annulation partielle indisponible: détail tarifaire manquant pour cette réservation.');
      return;
    }

    if (selectionSummary.totalParticipantsToCancel <= 0) {
      toast.error('Sélectionnez au moins une ligne tarifaire à annuler.');
      return;
    }

    if (shouldCollectRefundBankDetails) {
      const validationError = getOptionalRefundBankDetailsValidationError(normalizedIban, accountHolderName);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setBookingActionId(booking.id);
    try {
      const result = await bookingActionsService.cancelBookingParticipants({
        bookingId: booking.id,
        cancellationByRate: selectionSummary.cancellationByRate,
        idempotencyKey: partialCancellationDraft.idempotencyKey,
        reason: partialCancellationDraft.reason.trim() || undefined,
      });

      if (
        shouldCollectRefundBankDetails
        && result.partialRefundStatus === 'pending_customer_iban'
        && Number(result.refundAmountRequested || 0) > 0
        && shouldSubmitAdminRefundBankDetails(normalizedIban, accountHolderName)
      ) {
        try {
          await bookingActionsService.submitPartialRefundIban({
            bookingId: booking.id,
            iban: normalizedIban,
            accountHolderName,
          });
          await loadDashboardData(false);
          setPartialCancellationDraft(null);

          if (result.becameFullyCancelled) {
            toast.success('Tous les participants ont été annulés. Coordonnées de remboursement client enregistrées.');
          } else {
            toast.success(`${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s). Coordonnées de remboursement client enregistrées.`);
          }
          return;
        } catch (ibanError: unknown) {
          toast.error(getUserFacingErrorMessage(
            ibanError,
            'Annulation effectuée, mais impossible d\'enregistrer l\'IBAN client. Le dossier reste en attente d\'IBAN.',
            { audience: 'admin' },
          ));
        }
      }

      await loadDashboardData(false);
      setPartialCancellationDraft(null);

      if (result.becameFullyCancelled) {
        toast.success(result.refundRequired
          ? 'Tous les participants ont été annulés. Un remboursement reste à traiter.'
          : 'Tous les participants ont été annulés.');
      } else {
        toast.success(result.refundRequired
          ? `${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s). Un remboursement reste à traiter.`
          : `${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s).`);
      }
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible d’annuler ces participants.', { audience: 'admin' }));
    } finally {
      setBookingActionId(null);
    }
  };

  const handleCancelOuting = async (id: string) => {
    const outing = outings.find(o => o.id === id);
    if (!outing) return;

    setOrganizerMessageDraft('');
    organizerMessageRef.current = '';

    setConfirmDialog({
      title: 'Annuler cette expédition ?',
      message: `Cette action va annuler la sortie "${outing.title}" et prévenir les participants avec le modèle standard Jacana.`,
      confirmLabel: 'Annuler la sortie',
      tone: 'danger',
      showOrganizerMessageField: true,
      onConfirm: async () => {
        setOutingActionId(id);
        try {
          const result = await bookingActionsService.cancelOutingAndCascade({
            outingId: id,
            reason: 'organisation_cancellation',
            sendEmails: true,
            organizerMessage: organizerMessageRef.current.trim() || undefined,
          });

          await loadDashboardData(false);
          toast.success(`Sortie annulée. ${result.processedCount || 0} réservation(s) traitée(s).`);
        } catch (error: unknown) {
          console.error('Cancellation failed', error);
          toast.error(getUserFacingErrorMessage(error, "Erreur lors de l'annulation de la sortie.", { audience: 'admin' }));
        } finally {
          setOutingActionId(null);
        }
      },
    });
  };

  const handlePublishOuting = async (id: string) => {
    const outing = outings.find(o => o.id === id);
    if (!outing || outing.deleted) {
      toast.error('Impossible de publier cette sortie.');
      return;
    }

    setConfirmDialog({
      title: 'Publier cette sortie ? ',
      message: 'Publier cette sortie la rendra visible sur la page d’accueil client. Les emails et notifications ne sont pas encore envoyés à cette étape.',
      confirmLabel: 'Publier',
      tone: 'primary',
      onConfirm: async () => {
        setOutingActionId(id);
        try {
          const result = await outingPublicationService.publishOuting(id);
          await loadDashboardData(false);
          toast.success(result.status === 'already_published' ? 'Sortie déjà publiée' : 'Sortie publiée');
        } catch (error: unknown) {
          console.error('Publish outing failed', error);
          toast.error(getUserFacingErrorMessage(error, 'Impossible de publier cette sortie.', { audience: 'admin' }));
        } finally {
          setOutingActionId(null);
        }
      },
    });
  };

  const handleExportOutingReport = async (id: string) => {
    if (!id || exportingOutingId) return;

    setExportingOutingId(id);
    toast.loading('Génération du rapport Excel...', { id: 'outing-report-export' });

    try {
      await downloadOutingReportAsXlsx(id);
      toast.success('Rapport Excel téléchargé.', { id: 'outing-report-export' });
    } catch (error: unknown) {
      console.error('Outing report export failed', error);
      const message = getUserFacingErrorMessage(error, 'Impossible de générer le rapport Excel pour cette sortie.', { audience: 'admin' });
      toast.error(message, { id: 'outing-report-export' });
    } finally {
      setExportingOutingId(null);
    }
  };

  const handleDeleteOuting = async (id: string) => {
    const outing = outings.find(o => o.id === id);
    if (!outing) return;

    const hasBookings = bookings.some(b => b.outingId === id);
    if (hasBookings) {
      toast.error("Cette sortie a des participants. Utilisez l'annulation de sortie pour les prévenir.");
      return;
    }

    setConfirmDialog({
      title: 'Supprimer cette sortie vide ?',
      message: 'La suppression est définitive pour cette expédition sans réservation.',
      confirmLabel: 'Supprimer',
      tone: 'danger',
      onConfirm: async () => {
        setOutingActionId(id);
        try {
          await outingsService.delete(id);
          await loadDashboardData(false);
          toast.success('Sortie supprimée');
        } catch (error: unknown) {
          toast.error(getUserFacingErrorMessage(error, 'Erreur lors de la suppression.', { audience: 'admin' }));
        } finally {
          setOutingActionId(null);
        }
      },
    });
  };

  const handleOpenOuting = (id: string) => {
    setSelectedOutingId(id);
    setAnnouncementPreview(null);
    setAnnouncementPreviewError(null);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isOrgIbanInvalid = orgBankAccount.trim().length > 0 && !isValidBelgianIban(orgBankAccount);

  const handleSaveOrganizationBankAccount = async () => {
    if (isOrgIbanInvalid) {
      toast.error('IBAN belge invalide.');
      return;
    }

    if (!orgBankAccount.trim()) {
      toast.error('Le compte bancaire ne peut pas être vide.');
      return;
    }

    setSavingOrgBankAccount(true);
    try {
      await organizationService.upsertBankAccount(normalizeIban(orgBankAccount));
      setOrgBankAccount(normalizeIban(orgBankAccount));
      toast.success('Compte bancaire par défaut enregistré.');
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible d\'enregistrer le compte bancaire.', { audience: 'admin' }));
    } finally {
      setSavingOrgBankAccount(false);
    }
  };

  const handleBackToList = () => {
    setSelectedOutingId(null);
    setAnnouncementPreview(null);
    setAnnouncementPreviewError(null);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviewAnnouncementAudience = async (id: string) => {
    setAnnouncementPreviewLoading(true);
    setAnnouncementPreviewError(null);

    try {
      const result = await outingPublicationService.previewOutingPublicationAnnouncement(id);
      setAnnouncementPreview(result);
    } catch (error: unknown) {
      const message = getUserFacingErrorMessage(error, 'Impossible de calculer l’audience.', { audience: 'admin' });
      setAnnouncementPreview(null);
      setAnnouncementPreviewError(message);
      toast.error(message);
    } finally {
      setAnnouncementPreviewLoading(false);
    }
  };

  const handleSendAnnouncement = async (id: string) => {
    const outing = outings.find((item) => item.id === id);
    if (!outing || outing.deleted) {
      toast.error('Impossible d envoyer l’annonce pour cette sortie.');
      return;
    }

    setConfirmDialog({
      title: 'Envoyer l’annonce maintenant ? ',
      message: 'Cette action enverra immédiatement des emails newsletter et des notifications push aux personnes concernées.',
      confirmLabel: 'Envoyer l’annonce',
      tone: 'primary',
      onConfirm: async () => {
        setAnnouncementSendLoading(true);
        setOutingActionId(id);
        try {
          const result = await outingPublicationService.sendOutingPublicationAnnouncement(id);
          await loadDashboardData(false);
          toast.success(result.status === 'already_sent' ? 'Annonce déjà envoyée' : 'Annonce envoyée');
        } catch (error: unknown) {
          console.error('Send outing publication announcement failed', error);
          toast.error(getUserFacingErrorMessage(error, 'Impossible d’envoyer l’annonce.', { audience: 'admin' }));
        } finally {
          setAnnouncementSendLoading(false);
          setOutingActionId(null);
        }
      },
    });
  };

  const handleValidateManualPayment = async (booking: Booking) => {
    const moneySummary = getBookingMoneySummary(booking);
    if (!moneySummary) {
      toast.error('Montants indisponibles pour cette réservation. Validation manuelle bloquée.');
      return;
    }

    const amountMessage = `Montant à valider : ${moneySummary.currentTotalDue}€.`;

    setConfirmDialog({
      title: 'Valider ce virement manuel ?',
      message: `Vous confirmez la réception du virement pour ${booking.userName}. ${amountMessage} La réservation passera en "confirmée".`,
      confirmLabel: 'Valider le virement',
      tone: 'primary',
      onConfirm: async () => {
        setBookingActionId(booking.id);
        try {
          await bookingActionsService.validateManualPayment({ bookingId: booking.id });
          await loadDashboardData(false);
          toast.success('Virement validé et réservation confirmée.');
        } catch (error: unknown) {
          toast.error(getUserFacingErrorMessage(error, 'Impossible de valider ce virement.', { audience: 'admin' }));
        } finally {
          setBookingActionId(null);
        }
      },
    });
  };

  const handleMarkAttendance = async (booking: Booking, attendanceStatus: 'attended' | 'no_show') => {
    const label = attendanceStatus === 'attended' ? 'marquer comme présent' : 'marquer comme no-show';

    setConfirmDialog({
      title: 'Confirmer cette mise à jour ?',
      message: `Vous allez ${label} pour ${booking.userName}.`,
      confirmLabel: 'Confirmer',
      tone: 'primary',
      onConfirm: async () => {
        setBookingActionId(booking.id);
        try {
          await bookingActionsService.markAttendance({ bookingId: booking.id, attendanceStatus });
          await loadDashboardData(false);
          toast.success(attendanceStatus === 'attended' ? 'Présence enregistrée.' : 'No-show enregistré.');
        } catch (error: unknown) {
          toast.error(getUserFacingErrorMessage(error, 'Impossible de mettre à jour la présence.', { audience: 'admin' }));
        } finally {
          setBookingActionId(null);
        }
      },
    });
  };

  const handleProcessRefund = async (booking: Booking, action: 'refund' | 'refuse') => {
    const label = action === 'refund' ? 'déclencher le remboursement' : 'refuser la demande de remboursement';

    setConfirmDialog({
      title: 'Confirmer cette action ?',
      message: `Vous allez ${label} pour ${booking.userName}.`,
      confirmLabel: action === 'refund' ? 'Traiter le remboursement' : 'Refuser le remboursement',
      tone: action === 'refund' ? 'primary' : 'danger',
      onConfirm: async () => {
        setBookingActionId(booking.id);
        try {
          await bookingActionsService.processRefund({
            bookingId: booking.id,
            action,
            refundMethod: action === 'refund' ? 'manual' : undefined,
            refundReason: action === 'refund'
              ? 'Remboursement traité depuis le dashboard admin'
              : 'Demande refusée depuis le dashboard admin',
          });
          await loadDashboardData(false);
          toast.success(action === 'refund' ? 'Remboursement enregistré.' : 'Remboursement refusé.');
        } catch (error: unknown) {
          toast.error(getUserFacingErrorMessage(error, 'Impossible de traiter le remboursement.', { audience: 'admin' }));
        } finally {
          setBookingActionId(null);
        }
      },
    });
  };

  if (authLoading || loading) return (
    <div className="flex justify-center items-center h-screen bg-brand-paper">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent"></div>
    </div>
  );

  const totalRevenue = bookings
    .filter(b => resolvePaymentStatus(b) === 'paid')
    .reduce((acc, b) => acc + (getBookingMoneySummary(b as BookingWithLines)?.totalPaid || 0), 0);

  const pendingCount = bookings.filter(b => ['pending_payment', 'pending_validation'].includes(resolveBookingStatus(b))).length;
  const participantBreakdown = bookings.reduce((breakdown, booking) => {
    const bookingStatus = resolveBookingStatus(booking);
    if (['cancelled', 'expired', 'failed'].includes(bookingStatus)) return breakdown;
    const paymentGroup = resolvePaymentStatus(booking) === 'paid' ? 'paid' : 'pending';
    const lines = getActiveBookingLines(booking as BookingWithLines);
    const entries = lines.length > 0
      ? lines.map((line) => ({ label: line.labelSnapshot, quantity: line.quantityActive }))
      : [{ label: 'Tarif non détaillé', quantity: getActiveParticipantsCount(booking) }];
    entries.forEach(({ label, quantity }) => {
      if (quantity > 0) breakdown[paymentGroup][label] = (breakdown[paymentGroup][label] || 0) + quantity;
    });
    return breakdown;
  }, { paid: {} as Record<string, number>, pending: {} as Record<string, number> });
  const selectedOuting = selectedOutingId ? outings.find(o => o.id === selectedOutingId) : null;
  const selectedOutingCancelled = Boolean(selectedOuting?.deleted);
  const selectedOutingPublication = getPublicationBadgeProps(selectedOuting);
  const selectedOutingAnnouncementStatus = String(selectedOuting?.publicationAnnouncementStatus || 'not_started');
  const selectedOutingAnnouncementSummary = (selectedOuting?.publicationAnnouncementSummary || null) as OutingAnnouncementSummary;
  const outingBookings = selectedOutingId ? bookings.filter(b => b.outingId === selectedOutingId) : [];
  const selectedOutingHasBookings = outingBookings.length > 0;
  const adminButtonClass = 'flex min-h-[48px] items-center justify-center gap-3 rounded-full border px-5 py-3 text-center text-[11px] font-bold uppercase tracking-[0.16em] transition-all duration-500 sm:px-6 md:min-h-[44px] md:px-8 md:py-4 md:text-xs md:tracking-[0.18em]';

  const outingRevenue = outingBookings
    .filter(b => resolvePaymentStatus(b) === 'paid')
    .reduce((acc, b) => acc + (getBookingMoneySummary(b as BookingWithLines)?.totalPaid || 0), 0);
  const outingParticipants = outingBookings
    .filter(b => bookingOccupiesCapacity(resolveBookingStatus(b)))
    .reduce((acc, b) => acc + getActiveParticipantsCount(b), 0);
  const outingParticipantBreakdown = outingBookings.reduce((breakdown, booking) => {
    const bookingStatus = resolveBookingStatus(booking);
    if (['cancelled', 'expired', 'failed'].includes(bookingStatus)) return breakdown;
    const paymentGroup = resolvePaymentStatus(booking) === 'paid' ? 'paid' : 'pending';
    const lines = getActiveBookingLines(booking as BookingWithLines);
    const entries = lines.length > 0
      ? lines.map((line) => ({ label: line.labelSnapshot, quantity: line.quantityActive }))
      : [{ label: 'Tarif non détaillé', quantity: getActiveParticipantsCount(booking) }];
    entries.forEach(({ label, quantity }) => {
      if (quantity > 0) breakdown[paymentGroup][label] = (breakdown[paymentGroup][label] || 0) + quantity;
    });
    return breakdown;
  }, { paid: {} as Record<string, number>, pending: {} as Record<string, number> });
  const selectedOutingStartingPrice = selectedOuting ? getStartingPrice(selectedOuting.pricingConfig) : null;

  if (selectedOuting) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-7xl mx-auto space-y-10 px-4 py-10 pt-28 sm:px-6 sm:pt-32 md:space-y-12 md:px-6 md:py-12 md:pt-40 lg:pt-44"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <button 
            onClick={handleBackToList}
            className="group inline-flex min-h-[44px] items-center gap-3 self-start text-[11px] font-bold uppercase tracking-[0.16em] opacity-60 transition-all hover:opacity-100 md:gap-4 md:text-xs md:tracking-[0.18em]"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform duration-500" />
            Retour à la liste
          </button>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:flex xl:flex-wrap xl:justify-end">
            <button
              onClick={() => setSupportDialogOpen(true)}
              className={`${adminButtonClass} bg-amber-50 border-amber-100 text-amber-700 hover:bg-amber-100`}
            >
              <Bug size={14} />
              Signaler un incident
            </button>
            <button
              onClick={() => handleExportOutingReport(selectedOuting.id)}
              disabled={Boolean(exportingOutingId)}
              className={`${adminButtonClass} bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60`}
            >
              {exportingOutingId === selectedOuting.id ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
              Exporter le rapport Excel
            </button>
            <Link 
              to={`/admin/edit/${selectedOuting.id}`}
              className={`${adminButtonClass} bg-white border-brand-primary/10 hover:bg-brand-paper`}
            >
              <Edit size={14} />
              Modifier
            </Link>
            <HelpTrigger
              articleId="admin.outing.publication"
              category="admin.outing"
              label="Aide sur la publication"
            />
            {selectedOutingCancelled ? null : selectedOutingPublication.status === 'draft' ? (
              <button
                onClick={() => handlePublishOuting(selectedOuting.id)}
                disabled={outingActionId === selectedOuting.id}
                className={`${adminButtonClass} bg-sky-50 border-sky-100 text-sky-700 hover:bg-sky-100 disabled:opacity-60`}
              >
                {outingActionId === selectedOuting.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Publier
              </button>
            ) : selectedOutingPublication.status === 'published' ? (
              <div
                className={`${adminButtonClass} cursor-not-allowed bg-emerald-100 border-emerald-200 text-emerald-700`}
                aria-live="polite"
              >
                <Info size={14} />
                Déjà publiée
              </div>
            ) : (
              <div
                className={`${adminButtonClass} cursor-not-allowed bg-slate-100 border-slate-200 text-slate-700`}
                aria-live="polite"
              >
                <Info size={14} />
                Statut invalide
              </div>
            )}
            {selectedOutingCancelled ? (
              <div
                className={`${adminButtonClass} cursor-not-allowed bg-red-100 border-red-200 text-red-700`}
                aria-live="polite"
              >
                <XCircle size={14} />
                Sortie annulée
              </div>
            ) : selectedOutingHasBookings ? (
              <button 
                onClick={() => handleCancelOuting(selectedOuting.id)}
                disabled={outingActionId === selectedOuting.id}
                className={`${adminButtonClass} bg-red-50 border-red-100 text-red-600 hover:bg-red-100 disabled:opacity-60`}
              >
                {outingActionId === selectedOuting.id ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                Annuler l'expédition
              </button>
            ) : (
              <button 
                onClick={() => handleDeleteOuting(selectedOuting.id)}
                disabled={outingActionId === selectedOuting.id}
                className={`${adminButtonClass} bg-red-50 border-red-100 text-red-600 hover:bg-red-100 disabled:opacity-60`}
              >
                {outingActionId === selectedOuting.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Supprimer la sortie
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between md:gap-12">
          <div className="space-y-6">
            <h4 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-accent">Détails de l'expédition</h4>
            <h1 className="text-4xl font-serif font-bold italic leading-[1.1] text-brand-primary sm:text-5xl md:text-7xl">{selectedOuting.title}</h1>
            <div className="flex flex-col gap-3 text-lg font-serif italic text-brand-primary/50 sm:text-xl md:flex-row md:flex-wrap md:items-center md:gap-8">
              <span className="flex items-center gap-2"><CalendarIcon size={18} /> {format(new Date(selectedOuting.date), 'dd MMMM yyyy', { locale: fr })}</span>
              <span className="hidden md:inline">•</span>
              <span className="flex items-center gap-2"><MapPin size={18} /> {selectedOuting.location}</span>
            </div>
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] md:text-xs md:tracking-[0.16em] ${selectedOutingPublication.className}`}>
              <Info size={14} />
              {selectedOutingPublication.label}
            </div>
            {selectedOutingCancelled ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-100 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-red-700 md:text-xs md:tracking-[0.16em]">
                <XCircle size={14} />
                Cette sortie est annulée
              </div>
            ) : null}
          </div>
          <div className="w-full max-w-sm bg-brand-primary px-8 py-6 text-center text-white shadow-2xl rounded-[32px] sm:px-12 sm:py-8 md:w-auto md:rounded-[40px]">
            <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] opacity-60 mb-2">Tarif de départ</div>
            <div className="text-3xl font-serif font-bold italic sm:text-4xl">{selectedOutingStartingPrice !== null ? `${selectedOutingStartingPrice}€` : 'Tarifs indisponibles'}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-12">
          <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 space-y-5 shadow-sm sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
            <div className="flex items-center gap-4 text-brand-primary/30">
              <Users size={20} />
              <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">Participants</span>
            </div>
            <p className="text-4xl font-serif font-bold italic text-brand-primary sm:text-5xl">{outingParticipants} <span className="text-xl opacity-20 sm:text-2xl">/ {selectedOuting.maxParticipants}</span></p>
            <div className="w-full h-2 bg-brand-primary/5 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${(outingParticipants / selectedOuting.maxParticipants) * 100}%` }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="h-full bg-brand-accent" 
              />
            </div>
            <div className="grid grid-cols-1 gap-3 border-t border-brand-primary/5 pt-4 text-sm sm:grid-cols-2">
              {([['paid', 'Payés', 'text-emerald-800'], ['pending', 'En attente', 'text-brand-accent']] as const).map(([group, label, color]) => (
                <div key={group}>
                  <p className={`mb-2 text-[10px] font-bold uppercase tracking-[0.14em] ${color}`}>{label}</p>
                  <div className="space-y-1 text-brand-primary/70">
                    {Object.entries(outingParticipantBreakdown[group]).length > 0 ? Object.entries(outingParticipantBreakdown[group]).map(([category, count]) => (
                      <div key={category} className="flex justify-between gap-3">
                        <span className="truncate">{category}</span><span className="font-bold text-brand-primary">{count}</span>
                      </div>
                    )) : <span className="text-brand-primary/35">Aucun</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 space-y-5 shadow-sm sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
            <div className="flex items-center gap-4 text-brand-primary/30">
              <CreditCard size={20} />
              <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">Revenus encaissés</span>
            </div>
            <p className="text-4xl font-serif font-bold italic text-emerald-800 sm:text-5xl">{outingRevenue}€</p>
            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] opacity-55 text-brand-primary">{selectedOutingStartingPrice !== null ? `Base minimale: ${outingParticipants * selectedOutingStartingPrice}€` : 'Base minimale indisponible'}</p>
          </div>
          <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 space-y-5 shadow-sm sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
            <div className="flex items-center gap-4 text-brand-primary/30">
              <Clock size={20} />
              <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">Réservations</span>
            </div>
            <p className="text-4xl font-serif font-bold italic text-brand-primary sm:text-5xl">{outingBookings.length}</p>
            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] opacity-55 text-brand-primary">{outingBookings.filter(b => ['pending_payment', 'pending_validation'].includes(resolveBookingStatus(b))).length} dossier(s) à traiter</p>
          </div>
        </div>

        <div className="rounded-[32px] border border-brand-primary/10 bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-serif font-bold italic text-brand-primary">Aperçu annonce</h2>
                <HelpTrigger
                  articleId="admin.outing.publication"
                  category="admin.outing"
                  label="Aide sur la publication et les annonces"
                />
              </div>
              <p className="text-sm text-brand-primary/70">
                Cet aperçu ne déclenche aucun envoi. Il indique uniquement combien de personnes pourraient être informées.
              </p>
              <p className="text-sm text-brand-primary/70">
                Les emails newsletter et notifications push utilisent des audiences distinctes. Cette action est immédiate. Aucun brouillon n'est envoyé.
              </p>
            </div>

            {selectedOutingPublication.status === 'published' ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  onClick={() => handlePreviewAnnouncementAudience(selectedOuting.id)}
                  disabled={announcementPreviewLoading || announcementSendLoading || selectedOutingAnnouncementStatus === 'processing'}
                  className={`${adminButtonClass} bg-brand-paper border-brand-primary/10 text-brand-primary hover:bg-brand-paper/80 disabled:opacity-60`}
                >
                  {announcementPreviewLoading ? <Loader2 size={14} className="animate-spin" /> : <Info size={14} />}
                  Calculer l'audience
                </button>
                {selectedOutingAnnouncementStatus === 'completed' ? (
                  <div className={`${adminButtonClass} cursor-not-allowed bg-emerald-100 border-emerald-200 text-emerald-700`}>
                    <Info size={14} />
                    Annonce envoyée
                  </div>
                ) : selectedOutingAnnouncementStatus === 'processing' ? (
                  <div className={`${adminButtonClass} cursor-not-allowed bg-amber-100 border-amber-200 text-amber-700`}>
                    <Loader2 size={14} className="animate-spin" />
                    Envoi en cours
                  </div>
                ) : isTerminalAnnouncementStatus(selectedOutingAnnouncementStatus) ? (
                  <div className={`${adminButtonClass} cursor-not-allowed bg-slate-100 border-slate-200 text-slate-700`}>
                    <Info size={14} />
                    Déjà envoyée
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSendAnnouncement(selectedOuting.id)}
                    disabled={announcementSendLoading || outingActionId === selectedOuting.id}
                    className={`${adminButtonClass} bg-brand-primary border-brand-primary text-white hover:bg-brand-accent hover:border-brand-accent disabled:opacity-60`}
                  >
                    {announcementSendLoading && outingActionId === selectedOuting.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Envoyer l'annonce
                  </button>
                )}
              </div>
            ) : (
              <div className="rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-700 md:text-xs md:tracking-[0.16em]">
                L'audience sera calculable après publication.
              </div>
            )}
          </div>

          {selectedOutingPublication.status === 'published' ? (
            <div className="mt-6 space-y-4">
              {announcementPreviewError ? (
                <div className="rounded-3xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {announcementPreviewError}
                </div>
              ) : null}

              {announcementPreview ? (
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-3xl border border-brand-primary/10 bg-brand-paper px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">Abonnés newsletter email concernés</div>
                    <div className="mt-2 text-3xl font-serif font-bold italic text-brand-primary">{announcementPreview.emailTargetCount}</div>
                  </div>
                  <div className="rounded-3xl border border-brand-primary/10 bg-brand-paper px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">Utilisateurs push concernés</div>
                    <div className="mt-2 text-3xl font-serif font-bold italic text-brand-primary">{announcementPreview.pushUserTargetCount}</div>
                  </div>
                  <div className="rounded-3xl border border-brand-primary/10 bg-brand-paper px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">Tokens push disponibles</div>
                    <div className="mt-2 text-3xl font-serif font-bold italic text-brand-primary">{announcementPreview.pushTokenTargetCount}</div>
                  </div>
                </div>
              ) : (
                <div className="rounded-3xl border border-brand-primary/10 bg-brand-paper px-5 py-4 text-sm text-brand-primary/70">
                  Cliquez sur "Calculer l'audience" pour estimer séparément l'email newsletter et les notifications push.
                </div>
              )}

              {selectedOutingAnnouncementSummary ? (
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-3xl border border-brand-primary/10 bg-white px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">Emails envoyés</div>
                    <div className="mt-2 text-3xl font-serif font-bold italic text-brand-primary">{selectedOutingAnnouncementSummary.emailSucceeded}</div>
                    <div className="mt-2 text-sm text-brand-primary/65">Ciblés: {selectedOutingAnnouncementSummary.emailTargeted} • Échecs: {selectedOutingAnnouncementSummary.emailFailed}</div>
                  </div>
                  <div className="rounded-3xl border border-brand-primary/10 bg-white px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">Push envoyés</div>
                    <div className="mt-2 text-3xl font-serif font-bold italic text-brand-primary">{selectedOutingAnnouncementSummary.pushSucceeded}</div>
                    <div className="mt-2 text-sm text-brand-primary/65">Ciblés: {selectedOutingAnnouncementSummary.pushTargeted} • Échecs: {selectedOutingAnnouncementSummary.pushFailed}</div>
                  </div>
                  <div className="rounded-3xl border border-brand-primary/10 bg-white px-5 py-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">État d'annonce</div>
                    <div className="mt-2 text-2xl font-serif font-bold italic text-brand-primary">{selectedOuting?.publicationAnnouncementStatus || 'not_started'}</div>
                    <div className="mt-2 text-sm text-brand-primary/65">Dernier envoi: {selectedOuting?.publicationAnnouncementSentAt || 'Jamais'}</div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <ReservationsAdminList
          key={selectedOuting.id}
          bookings={outingBookings}
          loading={loading}
          busyId={bookingActionId}
          canValidateManualPayment={canValidateManualPayment}
          canCancelBookingParticipants={canCancelBookingParticipants}
          canCancelBooking={canCancelBooking}
          canMarkAttendance={canMarkAttendance}
          canProcessRefund={canProcessRefund}
          canProcessPartialRefund={canProcessPartialRefund}
          onValidateManualPayment={handleValidateManualPayment}
          onCancelBookingParticipants={openPartialCancellationDialog}
          onCancelBooking={openTotalCancellationDialog}
          onMarkAttendance={handleMarkAttendance}
          onProcessRefund={handleProcessRefund}
          onProcessPartialRefund={openPartialRefundDialog}
        />

        <SupportTicketDialog
          open={supportDialogOpen}
          onClose={() => setSupportDialogOpen(false)}
          variant="admin"
          role={role}
          moduleHint="admin-dashboard"
        />

        <ConfirmDialog
          open={Boolean(confirmDialog)}
          title={confirmDialog?.title || ''}
          message={confirmDialog?.message || ''}
          confirmLabel={confirmDialog?.confirmLabel}
          tone={confirmDialog?.tone}
          isLoading={Boolean(bookingActionId || outingActionId)}
          onConfirm={handleConfirmDialog}
          onClose={closeConfirmDialog}
        >
          {confirmDialog?.showOrganizerMessageField ? (
            <div className="space-y-2">
              <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-primary/70">
                Message aux participants (optionnel)
              </label>
              <textarea
                value={organizerMessageDraft}
                onChange={(event) => {
                  setOrganizerMessageDraft(event.target.value);
                  organizerMessageRef.current = event.target.value;
                }}
                maxLength={500}
                rows={4}
                placeholder="Ajoutez un contexte ou une précision à transmettre aux participants..."
                className="w-full rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
              />
              <p className="text-xs text-brand-primary/45 text-right">
                {organizerMessageDraft.trim().length}/500 caractères
              </p>
            </div>
          ) : null}
        </ConfirmDialog>

        <ConfirmDialog
          open={Boolean(totalCancellationDraft)}
          title="Annuler cette réservation ?"
          message={totalCancellationDraft
            ? `La réservation de ${totalCancellationDraft.booking.userName} sera annulée exactement selon les mêmes règles que côté client.`
            : ''}
          confirmLabel={totalCancellationDraft && shouldCollectRefundBankDetailsForTotal(totalCancellationDraft.booking)
            ? 'Annuler et enregistrer le remboursement'
            : "Confirmer l'annulation"}
          tone="danger"
          isLoading={Boolean(totalCancellationDraft?.booking?.id && bookingActionId === totalCancellationDraft.booking.id)}
          onConfirm={handleCancelBooking}
          onClose={() => {
            if (bookingActionId) return;
            setTotalCancellationDraft(null);
          }}
        >
          {totalCancellationDraft && shouldCollectRefundBankDetailsForTotal(totalCancellationDraft.booking) ? (
            <RefundBankDetailsSection
              estimatedAmount={getBookingMoneySummary(totalCancellationDraft.booking)?.totalRefundable ?? null}
              iban={totalCancellationDraft.iban}
              accountHolderName={totalCancellationDraft.accountHolderName}
              optional
              onIbanChange={(value) => {
                setTotalCancellationDraft((prev) => prev ? {
                  ...prev,
                  iban: value,
                } : prev);
              }}
              onAccountHolderNameChange={(value) => {
                setTotalCancellationDraft((prev) => prev ? {
                  ...prev,
                  accountHolderName: value,
                } : prev);
              }}
            />
          ) : null}
        </ConfirmDialog>

        <ConfirmDialog
          open={Boolean(partialRefundDraft)}
          title="Traiter le remboursement partiel"
          message={partialRefundDraft
            ? `Réservation de ${partialRefundDraft.booking.userName}. Les coordonnées bancaires complètes ne sont affichées que dans cette fenêtre.`
            : ''}
          confirmLabel="Marquer comme remboursé"
          tone="primary"
          isLoading={Boolean(partialRefundDraft?.booking?.id && bookingActionId === partialRefundDraft.booking.id)}
          onConfirm={handleMarkPartialRefunded}
          onClose={closePartialRefundDialog}
        >
          {partialRefundDraft ? (
            <div className="space-y-4">
              {partialRefundPayload ? (
                <>
                  <div className="rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary/85 space-y-2">
                    <p><strong>Bénéficiaire:</strong> {partialRefundPayload.beneficiary}</p>
                    <p><strong>IBAN:</strong> {partialRefundPayload.iban}</p>
                    <p><strong>Montant:</strong> {partialRefundPayload.amount} €</p>
                    <p><strong>Communication:</strong> {partialRefundPayload.communication}</p>
                  </div>

                  {partialRefundPayload.qrCodeDataUrl ? (
                    <div className="rounded-[20px] border border-brand-primary/10 bg-white px-4 py-4">
                      <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/60 mb-3">QR paiement</p>
                      <img
                        src={partialRefundPayload.qrCodeDataUrl}
                        alt="QR code de remboursement partiel"
                        className="mx-auto h-44 w-44 object-contain"
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm text-brand-primary/70">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement des informations bancaires sécurisées...
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                  Motif de refus (obligatoire si refus)
                </label>
                <textarea
                  value={partialRefundDraft.refusalReason}
                  onChange={(event) => {
                    const nextReason = event.target.value;
                    setPartialRefundDraft((prev) => (prev ? { ...prev, refusalReason: nextReason } : prev));
                  }}
                  maxLength={300}
                  rows={3}
                  placeholder="Ex: informations bancaires incohérentes"
                  className="w-full rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                />
              </div>

              <button
                type="button"
                onClick={handleRefusePartialRefund}
                disabled={Boolean(bookingActionId) || !partialRefundDraft.refusalReason.trim()}
                className="w-full sm:w-auto px-5 py-3 rounded-full text-sm font-bold tracking-[0.15em] uppercase transition-all shadow-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                Refuser le remboursement
              </button>
            </div>
          ) : null}
        </ConfirmDialog>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-7xl mx-auto space-y-10 px-4 py-10 pt-28 pb-24 sm:px-6 sm:pt-32 md:space-y-16 md:px-6 md:py-12 md:pt-40 md:pb-32 lg:pt-44"
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-8">
        <div className="space-y-4">
          <h4 className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-accent">Administration</h4>
          <h1 className="text-4xl font-serif font-bold italic text-brand-primary sm:text-5xl md:text-6xl">Tableau de bord</h1>
          <p className="text-lg font-serif italic text-brand-primary/40 sm:text-xl">Gérez vos expéditions et le suivi des explorateurs.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:flex md:flex-wrap md:justify-end md:gap-4">
          <button
            onClick={() => setSupportDialogOpen(true)}
            className={`${adminButtonClass} bg-amber-50 border-amber-100 text-amber-700 hover:bg-amber-100 shadow-sm`}
          >
            <Bug size={18} />
            Incident / amélioration
          </button>
          {role === 'Admin' ? (
            <Link
              to="/admin/users"
              className={`${adminButtonClass} bg-white border-brand-primary/10 text-brand-primary hover:bg-brand-paper shadow-sm`}
            >
              <Users size={18} />
              Utilisateurs
            </Link>
          ) : null}
          <Link
            to="/admin/home-settings"
            className={`${adminButtonClass} bg-white border-brand-primary/10 text-brand-primary hover:bg-brand-paper shadow-sm`}
          >
            <Settings2 size={18} />
            Parametres home
          </Link>
          <Link
            to="/admin/newsletter-info"
            className={`${adminButtonClass} bg-white border-brand-primary/10 text-brand-primary hover:bg-brand-paper shadow-sm`}
          >
            <Info size={18} />
            Info Newsletter
          </Link>
          <Link 
            to="/admin/create"
            className={`${adminButtonClass} bg-brand-primary border-brand-primary text-white hover:bg-brand-accent hover:border-brand-accent shadow-xl`}
          >
            <Plus size={18} />
            Nouvelle Expédition
          </Link>
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-12">
        <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 shadow-sm space-y-5 sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
          <div className="flex items-center gap-4 text-brand-primary/30">
            <Users size={20} />
            <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">Explorateurs cumulés</span>
          </div>
          <p className="text-4xl font-serif font-bold italic text-brand-primary sm:text-5xl">{bookings
            .filter((booking) => bookingOccupiesCapacity(resolveBookingStatus(booking)))
            .reduce((acc, booking) => acc + getActiveParticipantsCount(booking), 0)}
          </p>
          <div className="grid grid-cols-1 gap-4 border-t border-brand-primary/5 pt-4 text-sm sm:grid-cols-2">
            {([['paid', 'Payées', 'text-emerald-800'], ['pending', 'En attente de paiement', 'text-brand-accent']] as const).map(([group, label, color]) => (
              <div key={group}>
                <p className={`mb-2 text-[10px] font-bold uppercase tracking-[0.14em] ${color}`}>{label}</p>
                <div className="space-y-1 text-brand-primary/70">
                  {Object.entries(participantBreakdown[group]).length > 0 ? Object.entries(participantBreakdown[group]).map(([category, count]) => (
                    <div key={category} className="flex justify-between gap-3">
                      <span className="truncate">{category}</span><span className="font-bold text-brand-primary">{count}</span>
                    </div>
                  )) : <span className="text-brand-primary/35">Aucune</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 shadow-sm space-y-5 sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
          <div className="flex items-center gap-4 text-brand-primary/30">
            <CreditCard size={20} />
            <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">Revenus totaux</span>
          </div>
          <p className="text-4xl font-serif font-bold italic text-emerald-800 sm:text-5xl">{totalRevenue}€</p>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 shadow-sm space-y-5 sm:p-8 md:p-10 md:rounded-[40px] md:space-y-6">
          <div className="flex items-center gap-4 text-brand-primary/30">
            <Clock size={20} />
            <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em]">En attente</span>
          </div>
          <p className="text-4xl font-serif font-bold italic text-brand-accent sm:text-5xl">{pendingCount}</p>
        </div>
      </div>

      {/* Outings List (Only view now) */}
        <div className="hidden md:block bg-white rounded-[48px] border border-brand-primary/5 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-brand-paper/50 border-b border-brand-primary/5">
                <tr>
                  <th className="px-10 py-6 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Expédition</th>
                  <th className="px-10 py-6 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Date</th>
                  <th className="px-10 py-6 text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Remplissage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-primary/5">
                {outings.map((outing) => (
                  
                  <tr
                    key={outing.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpenOuting(outing.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenOuting(outing.id);
                      }
                    }}
                    className={`cursor-pointer focus:outline-none transition-colors duration-500 ${outing.deleted ? 'bg-red-50/60 hover:bg-red-100/70 focus:bg-red-100/70' : 'hover:bg-brand-paper focus:bg-brand-paper'}`}
                    aria-label={`Ouvrir l'administration de ${outing.title}`}
                  >
                    <td className="px-10 py-8">
                      <div className="flex items-center gap-6">
                        <img 
                          src={outing.imageUrl || `https://picsum.photos/seed/${outing.id}/100/100`} 
                          alt={outing.title} 
                          className={`w-16 h-16 rounded-3xl object-cover shadow-lg ${outing.deleted ? 'opacity-75 saturate-50' : ''}`}
                        />
                        <div>
                          {(() => {
                            const publicationBadge = getPublicationBadgeProps(outing);

                            return (
                              <div className="mb-2">
                                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${publicationBadge.className}`}>
                                  {publicationBadge.label}
                                </span>
                              </div>
                            );
                          })()}
                          <div className="flex items-center gap-3">
                            <div className="font-serif font-bold text-lg text-brand-primary">{outing.title}</div>
                            {outing.deleted ? (
                              <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-red-700">
                                Annulée
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] opacity-55 text-brand-primary mt-1">{outing.location}</div>
                          <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] text-brand-accent mt-2">Ouvrir la fiche d'administration</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-8">
                      <div className="font-serif italic text-lg text-brand-primary">{format(new Date(outing.date), 'dd MMMM yyyy', { locale: fr })}</div>
                    </td>
                    <td className="px-10 py-8">
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-2 bg-brand-primary/5 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-brand-accent transition-all duration-1000" 
                            style={{ width: `${(outing.currentParticipants / outing.maxParticipants) * 100}%` }}
                          />
                        </div>
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.12em] opacity-60 text-brand-primary">{outing.currentParticipants}/{outing.maxParticipants}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      <div className="grid grid-cols-1 gap-4 md:hidden">
        {outings.map((outing) => (
          <button
            key={outing.id}
            type="button"
            onClick={() => handleOpenOuting(outing.id)}
            className={`w-full rounded-[32px] border p-5 text-left shadow-sm transition-all duration-500 ${outing.deleted ? 'border-red-200 bg-red-50 hover:bg-red-100/70' : 'border-brand-primary/5 bg-white hover:bg-brand-paper'}`}
            aria-label={`Ouvrir l'administration de ${outing.title}`}
          >
            <div className="flex items-start gap-4">
              <img
                src={outing.imageUrl || `https://picsum.photos/seed/${outing.id}/100/100`}
                alt={outing.title}
                className={`h-16 w-16 shrink-0 rounded-3xl object-cover shadow-lg ${outing.deleted ? 'opacity-75 saturate-50' : ''}`}
              />
              <div className="min-w-0 flex-1 space-y-2">
                {(() => {
                  const publicationBadge = getPublicationBadgeProps(outing);

                  return (
                    <div>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${publicationBadge.className}`}>
                        {publicationBadge.label}
                      </span>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2">
                  <div className="font-serif text-xl font-bold text-brand-primary">{outing.title}</div>
                  {outing.deleted ? (
                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-red-700">
                      Annulée
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-primary/55">{outing.location}</div>
                <div className="font-serif text-base italic text-brand-primary/70">{format(new Date(outing.date), 'dd MMMM yyyy', { locale: fr })}</div>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-primary/60">
                <span>Remplissage</span>
                <span>{outing.currentParticipants}/{outing.maxParticipants}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-brand-primary/5">
                <div
                  className="h-full bg-brand-accent transition-all duration-1000"
                  style={{ width: `${(outing.currentParticipants / outing.maxParticipants) * 100}%` }}
                />
              </div>
              <div className="pt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-accent">
                Ouvrir la fiche d'administration
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white p-6 rounded-[32px] border border-brand-primary/5 shadow-sm space-y-4 sm:p-8 md:rounded-[40px]">
        <div>
          <h3 className="text-2xl font-serif font-bold italic text-brand-primary">Compte bancaire organisation</h3>
          <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/55 mt-2">
            Utilise par defaut pour les paiements par virement
          </p>
        </div>

        <div className="max-w-xl space-y-3">
          <TextField
            fullWidth
            label="Compte bancaire (IBAN)"
            value={orgBankAccount}
            onChange={(event) => setOrgBankAccount(event.target.value.toUpperCase())}
            error={isOrgIbanInvalid}
            helperText={isOrgIbanInvalid ? 'IBAN belge invalide' : 'Format attendu : BE + 14 chiffres'}
          />
          <Button
            variant="contained"
            onClick={handleSaveOrganizationBankAccount}
            disabled={savingOrgBankAccount || isOrgIbanInvalid}
          >
            {savingOrgBankAccount ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <SupportTicketDialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
        variant="admin"
        role={role}
        moduleHint="admin-dashboard"
      />

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel}
        tone={confirmDialog?.tone}
        isLoading={Boolean(bookingActionId || outingActionId)}
        onConfirm={handleConfirmDialog}
        onClose={closeConfirmDialog}
      >
        {confirmDialog?.showOrganizerMessageField ? (
          <div className="space-y-2">
            <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-primary/70">
              Message aux participants (optionnel)
            </label>
            <textarea
              value={organizerMessageDraft}
              onChange={(event) => {
                setOrganizerMessageDraft(event.target.value);
                organizerMessageRef.current = event.target.value;
              }}
              maxLength={500}
              rows={4}
              placeholder="Ajoutez un contexte ou une précision à transmettre aux participants..."
              className="w-full rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
            />
            <p className="text-xs text-brand-primary/45 text-right">
              {organizerMessageDraft.trim().length}/500 caractères
            </p>
          </div>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(partialCancellationDraft)}
        title="Annuler des participants ?"
        message={partialCancellationDraft
          ? `Réservation de ${partialCancellationDraft.booking.userName}. Cette action mettra à jour automatiquement la réservation.`
          : ''}
        confirmLabel={partialCancellationDraft
          ? (() => {
            const summary = summarizePartialCancellationSelection(
              partialCancellationDraft.booking,
              partialCancellationDraft.cancellationByRate,
            );

            if (summary.totalParticipantsToCancel <= 0) {
              return null;
            }

            return summary.totalParticipantsToCancel >= getActiveParticipantsCount(partialCancellationDraft.booking)
              ? 'Annuler toute la réservation'
              : `Annuler ${summary.totalParticipantsToCancel} participant(s)`;
          })()
          : 'Confirmer'}
        tone="danger"
        isLoading={Boolean(partialCancellationDraft?.booking?.id && bookingActionId === partialCancellationDraft.booking.id)}
        onConfirm={handleCancelBookingParticipants}
        onClose={() => {
          if (bookingActionId) return;
          setPartialCancellationDraft(null);
        }}
      >
        {partialCancellationDraft ? (
          <div className="space-y-4">
            {(() => {
              const summary = summarizePartialCancellationSelection(
                partialCancellationDraft.booking,
                partialCancellationDraft.cancellationByRate,
              );

              return (
                <>
                  <div className="space-y-3">
                    <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                      Lignes tarifaires actives
                    </label>

                    {summary.activeLines.map((line) => {
                      const selectedQuantity = partialCancellationDraft.cancellationByRate[line.rateId] ?? 0;

                      return (
                        <div
                          key={line.rateId}
                          className="flex flex-col gap-3 rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-brand-primary">{line.labelSnapshot}</p>
                            <p className="text-xs text-brand-primary/65">
                              {line.quantityActive} actif(s) • {line.unitPriceSnapshot > 0 ? `${line.unitPriceSnapshot} € / participant` : 'Gratuit'}
                            </p>
                          </div>

                          <div className="w-full md:w-28">
                            <input
                              type="number"
                              min={0}
                              max={line.quantityActive}
                              value={selectedQuantity}
                              onChange={(event) => {
                                const nextValue = Number.parseInt(event.target.value, 10);
                                const normalized = Number.isFinite(nextValue)
                                  ? Math.max(0, Math.min(line.quantityActive, nextValue))
                                  : 0;

                                setPartialCancellationDraft((prev) => {
                                  if (!prev) return prev;

                                  const nextCancellationByRate = { ...prev.cancellationByRate };
                                  if (normalized > 0) {
                                    nextCancellationByRate[line.rateId] = normalized;
                                  } else {
                                    delete nextCancellationByRate[line.rateId];
                                  }

                                  return {
                                    ...prev,
                                    cancellationByRate: nextCancellationByRate,
                                  };
                                });
                              }}
                              className="w-full rounded-[20px] border border-brand-primary/10 bg-white px-4 py-3 text-sm text-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary/80 leading-relaxed space-y-2">
                    <p>
                      Résumé: {summary.totalParticipantsToCancel} participant(s) annulé(s),{' '}
                      {Math.max(0, getActiveParticipantsCount(partialCancellationDraft.booking) - summary.totalParticipantsToCancel)} actif(s) restant(s)
                      {' '}sur {getTotalParticipantsCount(partialCancellationDraft.booking)}.
                    </p>
                    <p>
                      Remboursement estimé: <strong>{summary.estimatedRefund} €</strong>
                    </p>
                    {summary.totalParticipantsToCancel <= 0 ? (
                      <p className="text-red-700 font-semibold">
                        Sélectionnez au moins une ligne tarifaire à annuler.
                      </p>
                    ) : null}
                    {summary.totalParticipantsToCancel >= getActiveParticipantsCount(partialCancellationDraft.booking) ? (
                      <p className="text-red-700 font-semibold">
                        Cette sélection annulera toute la réservation.
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                      Raison (optionnelle)
                    </label>
                    <textarea
                      value={partialCancellationDraft.reason}
                      onChange={(event) => {
                        const nextReason = event.target.value;
                        setPartialCancellationDraft((prev) => prev ? {
                          ...prev,
                          reason: nextReason,
                        } : prev);
                      }}
                      maxLength={300}
                      rows={4}
                      placeholder="Ex: 2 participants indisponibles"
                      className="w-full rounded-[20px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                    />
                  </div>

                  {shouldCollectRefundBankDetailsForPartial(partialCancellationDraft.booking, partialCancellationDraft.cancellationByRate) ? (
                    <RefundBankDetailsSection
                      estimatedAmount={summary.estimatedRefund}
                      optional
                      iban={partialCancellationDraft.iban}
                      accountHolderName={partialCancellationDraft.accountHolderName}
                      onIbanChange={(value) => {
                        setPartialCancellationDraft((prev) => prev ? {
                          ...prev,
                          iban: value,
                        } : prev);
                      }}
                      onAccountHolderNameChange={(value) => {
                        setPartialCancellationDraft((prev) => prev ? {
                          ...prev,
                          accountHolderName: value,
                        } : prev);
                      }}
                    />
                  ) : null}
                </>
              );
            })()}
          </div>
        ) : null}
      </ConfirmDialog>
    </motion.div>
  );
}
